import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
} from '@/components/ui/dropdown-menu';
import { Bell, CheckCheck, Inbox } from 'lucide-react';

/**
 * Bell icon + dropdown for in-app notifications.
 *
 * Polls unread-count every 60s while the user is authenticated. Full
 * list is fetched only when the dropdown is opened (cheap on our side,
 * and the user rarely reads passively).
 *
 * Clicking a notification marks it read AND navigates to its `link`.
 * "Mark all read" clears the badge in one call.
 */
const POLL_INTERVAL_MS = 60_000;

const iconForKind = (kind) => {
  // Kept tiny — the drop-down list looks clean when items don't all
  // fight for attention with different colours.
  switch (kind) {
    case 'listing_approved':
    case 'verification_approved':
    case 'payment_confirmed':
      return { emoji: '✅', color: 'text-emerald-600' };
    case 'listing_rejected':
    case 'verification_rejected':
      return { emoji: '⚠️', color: 'text-red-600' };
    case 'new_inquiry':
    case 'new_follower':
      return { emoji: '📩', color: 'text-blue-600' };
    case 'review_received':
      return { emoji: '⭐', color: 'text-amber-600' };
    case 'reward_earned':
    case 'referral_reward':
      return { emoji: '🎁', color: 'text-orange-600' };
    case 'announcement':
      return { emoji: '📣', color: 'text-primary' };
    default:
      return { emoji: '🔔', color: 'text-slate-600' };
  }
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.max(1, Math.round((now - then) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
};

const NotificationBell = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const pollRef = useRef(null);

  const fetchUnread = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get('/notifications-inbox/unread-count');
      setUnread(data?.count || 0);
    } catch {
      // silent — bell just shows previous count
    }
  }, [user]);

  const fetchList = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data } = await api.get('/notifications-inbox', { params: { limit: 20 } });
      setItems(data?.items || []);
      setUnread(data?.unread_count || 0);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      setUnread(0);
      setItems([]);
      return () => {};
    }
    fetchUnread();
    pollRef.current = setInterval(fetchUnread, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [user, fetchUnread]);

  useEffect(() => {
    if (open) fetchList();
  }, [open, fetchList]);

  const clickItem = async (item) => {
    setOpen(false);
    if (!item.read) {
      try {
        await api.post(`/notifications-inbox/${item.id}/read`);
        setUnread(u => Math.max(0, u - 1));
      } catch {
        // ignore — worst case the item stays unread
      }
    }
    if (item.link) navigate(item.link);
  };

  const markAllRead = async () => {
    try {
      await api.post('/notifications-inbox/read-all');
      setUnread(0);
      setItems(prev => prev.map(i => ({ ...i, read: true })));
    } catch {
      // ignore
    }
  };

  // Hide entirely for logged-out users to keep the header clean.
  if (!user) return null;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type='button'
          className='relative inline-flex items-center justify-center h-9 w-9 rounded-md hover:bg-secondary'
          aria-label='Notifications'
          data-testid='notification-bell'
        >
          <Bell className='h-5 w-5 text-muted-foreground' />
          {unread > 0 && (
            <span
              className='absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center'
              data-testid='notification-bell-count'
            >
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align='end'
        className='w-80 sm:w-96 p-0'
        data-testid='notification-dropdown'
      >
        <div className='flex items-center justify-between px-3 py-2.5 border-b'>
          <div className='font-semibold text-sm'>Notifications</div>
          {items.some(i => !i.read) && (
            <button
              type='button'
              onClick={markAllRead}
              className='text-xs text-primary inline-flex items-center gap-1 hover:underline'
              data-testid='notification-mark-all-read'
            >
              <CheckCheck className='h-3.5 w-3.5' /> Mark all read
            </button>
          )}
        </div>
        <div className='max-h-96 overflow-y-auto'>
          {loading ? (
            <div className='px-3 py-8 text-center text-xs text-muted-foreground'>Loading…</div>
          ) : items.length === 0 ? (
            <div className='px-3 py-10 text-center text-xs text-muted-foreground'>
              <Inbox className='h-6 w-6 mx-auto mb-2 opacity-60' />
              You&apos;re all caught up.
            </div>
          ) : (
            items.map(item => {
              const iconMeta = iconForKind(item.kind);
              return (
                <button
                  key={item.id}
                  type='button'
                  onClick={() => clickItem(item)}
                  className={`w-full text-left px-3 py-2.5 border-b last:border-b-0 hover:bg-secondary/60 transition-colors ${item.read ? '' : 'bg-blue-50/50'}`}
                  data-testid={`notification-item-${item.id}`}
                >
                  <div className='flex items-start gap-2.5'>
                    <span className={`text-lg leading-none ${iconMeta.color}`}>{iconMeta.emoji}</span>
                    <div className='flex-1 min-w-0'>
                      <div className='flex items-center gap-2'>
                        <div className={`text-sm ${item.read ? 'text-foreground/90' : 'font-semibold text-foreground'} truncate`}>
                          {item.title}
                        </div>
                        {!item.read && (
                          <span className='h-1.5 w-1.5 rounded-full bg-blue-600 shrink-0' aria-hidden />
                        )}
                      </div>
                      {item.body && (
                        <div className='text-xs text-muted-foreground mt-0.5 line-clamp-2'>
                          {item.body}
                        </div>
                      )}
                      <div className='text-[10px] text-muted-foreground/80 mt-1'>
                        {timeAgo(item.created_at)}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationBell;
